import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { calculatePoints } from '../utils/scoring';
import { calculateAthleteStats } from '../utils/statistics';

const MatchContext = createContext(null);

const STORAGE_KEY = 'tkd_session_v1';

const initialState = {
  // Setup sessione
  session: null,
  isSetupComplete: false,

  // Timer
  timer: { seconds: 0, isRunning: false, display: '00:00.0' },
  currentRound: 1,

  // Azione corrente in composizione
  currentAction: {
    athlete: null,
    actionType: null,
    technique: null,
    customTechnique: '',
    legSide: null,
    legPosition: null,
    guard: null,
    distance: null,
    target: null,
    outcome: null,
    points: 0,
    technicalBonus: false,
    timestamp_start: null,
    timestamp_end: null,
  },

  // ID azione in modifica (null = nuova azione)
  editingActionId: null,

  // Dati partita
  actions: [],
  penalties: [],

  // Scores calcolati
  scores: {
    blue: 0,
    red: 0,
    byRound: [],
  },

  // Round markers (inizio/fine round nel video)
  roundMarkers: [],

  // Video
  videoMode: 'split',
  videoTimestamp: 0,

  // Stats live
  stats: {
    blue: { totalActions: 0, attackEfficiency: 0, defenseEfficiency: 0, techniqueCount: {} },
    red:  { totalActions: 0, attackEfficiency: 0, defenseEfficiency: 0, techniqueCount: {} },
  },
};

function computeScores(actions, penalties, numRounds = 3) {
  let blueTotal = 0;
  let redTotal  = 0;
  const byRound = [];

  for (let r = 1; r <= numRounds; r++) {
    const rActs = actions.filter(a => a.round_number === r);
    const rPens = penalties.filter(p => p.round_number === r);
    const blueAct = rActs.filter(a => a.athlete === 'blue' && a.outcome === 'punto').reduce((s,a) => s+(a.points||0),0);
    const redAct  = rActs.filter(a => a.athlete === 'red'  && a.outcome === 'punto').reduce((s,a) => s+(a.points||0),0);
    const bluePen = rPens.filter(p => p.athlete === 'blue').length; // blue's gam-jeom → red gets pts
    const redPen  = rPens.filter(p => p.athlete === 'red').length;  // red's gam-jeom  → blue gets pts
    const blueR = blueAct + redPen;
    const redR  = redAct  + bluePen;
    blueTotal += blueR;
    redTotal  += redR;
    byRound.push({ round: r, blue: blueR, red: redR });
  }
  return { blue: blueTotal, red: redTotal, byRound };
}

// ── Persistenza localStorage ──────────────────────────────────────────────────

export function clearSavedSession() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

function loadPersistedState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState;
    const saved = JSON.parse(raw);
    if (!saved?.isSetupComplete || !saved?.session) return initialState;

    const actions   = saved.actions   || [];
    const penalties = saved.penalties || [];
    const numRounds = saved.session.num_rounds || 3;
    const scores    = computeScores(actions, penalties, numRounds);
    const stats = {
      blue: calculateAthleteStats(actions, 'blue'),
      red:  calculateAthleteStats(actions, 'red'),
    };

    return {
      ...initialState,
      session:         saved.session,
      isSetupComplete: true,
      currentRound:    saved.currentRound || 1,
      actions,
      penalties,
      scores,
      stats,
      roundMarkers:    saved.roundMarkers || [],
    };
  } catch {
    return initialState;
  }
}

// ─────────────────────────────────────────────────────────────────────────────

function reducer(state, action) {
  switch (action.type) {
    case 'SETUP_COMPLETE':
      return { ...state, session: action.session, isSetupComplete: true };

    case 'UPDATE_SESSION':
      return { ...state, session: { ...state.session, ...action.fields } };

    case 'SET_ROUND':
      return { ...state, currentRound: action.round };

    case 'SET_TIMER':
      return { ...state, timer: { ...state.timer, ...action.timer } };

    case 'SET_VIDEO_TIMESTAMP':
      return { ...state, videoTimestamp: action.timestamp };

    case 'SET_VIDEO_MODE':
      return { ...state, videoMode: action.mode };

    case 'SELECT_ATHLETE': {
      const pts = calculatePoints(
        state.currentAction.technique,
        state.currentAction.target,
        state.currentAction.outcome,
        state.currentAction.technicalBonus
      );
      return {
        ...state,
        currentAction: { ...state.currentAction, athlete: action.athlete, points: pts },
      };
    }

    case 'SET_ACTION_FIELD': {
      const updated = { ...state.currentAction, [action.field]: action.value };
      // Recalculate points on relevant field changes
      const pts = calculatePoints(updated.technique, updated.target, updated.outcome, updated.technicalBonus);
      updated.points = pts;
      return { ...state, currentAction: updated };
    }

    case 'CONFIRM_ACTION': {
      const newActions = [...state.actions, action.action];
      const numRounds  = state.session?.num_rounds || 3;
      const scores     = computeScores(newActions, state.penalties, numRounds);
      const stats = {
        blue: calculateAthleteStats(newActions, 'blue'),
        red:  calculateAthleteStats(newActions, 'red'),
      };
      return {
        ...state,
        actions: newActions,
        scores,
        stats,
        currentAction: { ...initialState.currentAction },
      };
    }

    case 'DELETE_ACTION': {
      const newActions = state.actions.filter(a => a.id !== action.id);
      const numRounds  = state.session?.num_rounds || 3;
      const scores     = computeScores(newActions, state.penalties, numRounds);
      const stats = {
        blue: calculateAthleteStats(newActions, 'blue'),
        red:  calculateAthleteStats(newActions, 'red'),
      };
      return { ...state, actions: newActions, scores, stats };
    }

    case 'UPDATE_ACTION': {
      const newActions = state.actions.map(a => a.id === action.id ? { ...a, ...action.fields } : a);
      const numRounds  = state.session?.num_rounds || 3;
      const scores     = computeScores(newActions, state.penalties, numRounds);
      const stats = {
        blue: calculateAthleteStats(newActions, 'blue'),
        red:  calculateAthleteStats(newActions, 'red'),
      };
      return { ...state, actions: newActions, scores, stats };
    }

    case 'ADD_PENALTY': {
      const newPenalties = [...state.penalties, action.penalty];
      const numRounds    = state.session?.num_rounds || 3;
      const scores       = computeScores(state.actions, newPenalties, numRounds);
      return { ...state, penalties: newPenalties, scores };
    }

    case 'DELETE_PENALTY': {
      const newPenalties = state.penalties.filter(p => p.id !== action.id);
      const numRounds    = state.session?.num_rounds || 3;
      const scores       = computeScores(state.actions, newPenalties, numRounds);
      return { ...state, penalties: newPenalties, scores };
    }

    case 'MARK_ROUND': {
      // Sostituisce eventuale marker precedente stesso round+tipo
      const filtered = state.roundMarkers.filter(
        m => !(m.round === action.marker.round && m.type === action.marker.type)
      );
      return { ...state, roundMarkers: [...filtered, action.marker] };
    }

    case 'DELETE_ROUND_MARKER':
      return { ...state, roundMarkers: state.roundMarkers.filter(m => m.id !== action.id) };

    case 'RESET_ACTION':
      return { ...state, currentAction: { ...initialState.currentAction }, editingActionId: null };

    case 'LOAD_ACTION_FOR_EDIT': {
      const a = action.action;
      // Le azioni locali usano camelCase, quelle remote snake_case — gestiamo entrambe
      return {
        ...state,
        editingActionId: a.id,
        currentAction: {
          athlete:         a.athlete,
          actionType:      a.action_type      ?? a.actionType      ?? null,
          technique:       a.technique        ?? null,
          customTechnique: a.custom_technique ?? a.customTechnique ?? '',
          legSide:         a.leg_side         ?? a.legSide         ?? null,
          legPosition:     a.leg_position     ?? a.legPosition     ?? null,
          guard:           a.guard            ?? null,
          distance:        a.distance         ?? null,
          target:          a.target           ?? null,
          outcome:         a.outcome          ?? null,
          points:          a.points           ?? 0,
          technicalBonus:  a.technical_bonus !== undefined ? !!a.technical_bonus : !!a.technicalBonus,
          timestamp_start: a.timestamp_start  ?? null,
          timestamp_end:   a.timestamp_end    ?? null,
        },
      };
    }

    case 'CLEAR_EDIT_MODE':
      return { ...state, editingActionId: null, currentAction: { ...initialState.currentAction } };

    case 'LOAD_SESSION': {
      const { session, actions, penalties } = action;
      const numRounds = session.num_rounds || 3;
      const scores    = computeScores(actions, penalties, numRounds);
      const stats = {
        blue: calculateAthleteStats(actions, 'blue'),
        red:  calculateAthleteStats(actions, 'red'),
      };
      return {
        ...state,
        session,
        isSetupComplete: true,
        actions,
        penalties,
        scores,
        stats,
      };
    }

    default:
      return state;
  }
}

export function MatchProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, undefined, loadPersistedState);

  // Salva su localStorage ad ogni cambio rilevante
  useEffect(() => {
    if (!state.isSetupComplete) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        session:         state.session,
        isSetupComplete: state.isSetupComplete,
        currentRound:    state.currentRound,
        actions:         state.actions,
        penalties:       state.penalties,
        roundMarkers:    state.roundMarkers,
      }));
    } catch {}
  }, [state.isSetupComplete, state.session, state.currentRound, state.actions, state.penalties]);

  const selectAthlete = useCallback((athlete) => {
    dispatch({ type: 'SELECT_ATHLETE', athlete });
  }, []);

  const setActionField = useCallback((field, value) => {
    dispatch({ type: 'SET_ACTION_FIELD', field, value });
  }, []);

  const confirmAction = useCallback((action) => {
    dispatch({ type: 'CONFIRM_ACTION', action });
  }, []);

  const deleteAction = useCallback((id) => {
    dispatch({ type: 'DELETE_ACTION', id });
  }, []);

  const updateAction = useCallback((id, fields) => {
    dispatch({ type: 'UPDATE_ACTION', id, fields });
  }, []);

  const addPenalty = useCallback((penalty) => {
    dispatch({ type: 'ADD_PENALTY', penalty });
  }, []);

  const deletePenalty = useCallback((id) => {
    dispatch({ type: 'DELETE_PENALTY', id });
  }, []);

  const resetAction = useCallback(() => {
    dispatch({ type: 'RESET_ACTION' });
  }, []);

  const loadActionForEdit = useCallback((action) => {
    dispatch({ type: 'LOAD_ACTION_FOR_EDIT', action });
  }, []);

  const clearEditMode = useCallback(() => {
    dispatch({ type: 'CLEAR_EDIT_MODE' });
  }, []);

  const setupComplete = useCallback((session) => {
    dispatch({ type: 'SETUP_COMPLETE', session });
  }, []);

  const updateSession = useCallback((fields) => {
    dispatch({ type: 'UPDATE_SESSION', fields });
  }, []);

  const markRound = useCallback((marker) => {
    dispatch({ type: 'MARK_ROUND', marker });
  }, []);

  const deleteRoundMarker = useCallback((id) => {
    dispatch({ type: 'DELETE_ROUND_MARKER', id });
  }, []);

  const loadSession = useCallback((session, actions, penalties) => {
    dispatch({ type: 'LOAD_SESSION', session, actions, penalties });
  }, []);

  const setTimer = useCallback((timer) => {
    dispatch({ type: 'SET_TIMER', timer });
  }, []);

  const setVideoTimestamp = useCallback((timestamp) => {
    dispatch({ type: 'SET_VIDEO_TIMESTAMP', timestamp });
  }, []);

  const setVideoMode = useCallback((mode) => {
    dispatch({ type: 'SET_VIDEO_MODE', mode });
  }, []);

  const setRound = useCallback((round) => {
    dispatch({ type: 'SET_ROUND', round });
  }, []);

  return (
    <MatchContext.Provider value={{
      state,
      dispatch,
      selectAthlete,
      setActionField,
      confirmAction,
      deleteAction,
      updateAction,
      addPenalty,
      deletePenalty,
      resetAction,
      loadActionForEdit,
      clearEditMode,
      setupComplete,
      updateSession,
      markRound,
      deleteRoundMarker,
      loadSession,
      setTimer,
      setVideoTimestamp,
      setVideoMode,
      setRound,
    }}>
      {children}
    </MatchContext.Provider>
  );
}

export function useMatch() {
  const ctx = useContext(MatchContext);
  if (!ctx) throw new Error('useMatch deve essere usato dentro MatchProvider');
  return ctx;
}

export default MatchContext;
